import React, { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { supabase } from '@/lib/supabase';
import { createCheckIn } from '@/lib/checkin';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Swords, MapPin, Clock, X, Users, Info, AlertTriangle, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { useOptimizedGeolocation } from '@/hooks/useOptimizedGeolocation';
import { useNavigate } from 'react-router-dom';

function calculateDistance(lat1, lng1, lat2, lng2) {
  const R = 6371; // Earth radius in km
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lng2 - lng1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.asin(Math.sqrt(a));

  return R * c; // Distance in km
}

const OPEN_DUEL_DURATION_MS = 2 * 60 * 60 * 1000;

function parseDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return isNaN(date.getTime()) ? null : date;
}

function getOpenStartDate(duel) {
  return (
    parseDate(duel.refused_at) ||
    parseDate(duel.opened_at) ||
    parseDate(duel.accept_deadline_at) ||
    parseDate(duel.slot_selection_deadline_at) ||
    parseDate(duel.updated_date) ||
    parseDate(duel.created_at) ||
    parseDate(duel.created_date)
  );
}

function getOpenExpiresAt(duel) {
  const explicit = parseDate(duel.open_expires_at);
  if (explicit) return explicit;
  const start = getOpenStartDate(duel);
  if (!start) return null;
  return new Date(start.getTime() + OPEN_DUEL_DURATION_MS);
}

function formatTimeRemaining(expiresAt) {
  if (!expiresAt) return 'Non défini';

  const now = new Date();
  const expires = expiresAt instanceof Date ? expiresAt : new Date(expiresAt);

  // Check if date is valid
  if (isNaN(expires.getTime())) return 'Non défini';

  const diff = expires - now;

  if (diff <= 0) return 'Expiré';

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
}

export default function OpenDuels({ onClose, userLocation: propUserLocation }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [joiningDuelId, setJoiningDuelId] = useState(null);
  const [userLocation, setUserLocation] = useState(propUserLocation || null);
  const queryClient = useQueryClient();
  const { getLocation } = useOptimizedGeolocation();

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user: authUser } }) => setUser(authUser)).catch(() => setUser(null));
  }, []);

  // Get user location if not provided as prop (standalone mode)
  useEffect(() => {
    if (!propUserLocation) {
      getLocation({ useCache: true, timeout: 3000 })
        .then(location => {
          setUserLocation({ lat: location.lat, lng: location.lng });
        })
        .catch(err => {
          console.warn('Location failed:', err);
        });
    }
  }, [propUserLocation, getLocation]);

  // Auto-scroll to bottom on mobile
  useEffect(() => {
    if (window.innerWidth < 768) {
      setTimeout(() => {
        window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
      }, 100);
    }
  }, []);

  // Fetch all OPEN duels
  const { data: openDuels = [], refetch } = useQuery({
    queryKey: ['openDuels'],
    queryFn: async () => {
      const duels = await base44.entities.CourtDuel.filter({
        status: 'OPEN'
      });
      return duels;
    },
    refetchInterval: 30000 // Refresh every 30s
  });

  // Fetch playgrounds for OPEN duels
  const { data: playgroundsMap = {} } = useQuery({
    queryKey: ['openDuelsPlaygrounds', openDuels.length],
    queryFn: async () => {
      if (openDuels.length === 0) return {};

      const playgroundIds = [...new Set(openDuels.map(d => d.playground_id))];
      const playgrounds = await Promise.all(
        playgroundIds.map(id =>
          base44.entities.Playground.filter({ id }).then(p => p[0])
        )
      );

      const map = {};
      playgrounds.forEach(p => {
        if (p) map[p.id] = p;
      });
      return map;
    },
    enabled: openDuels.length > 0
  });

  // Enrich duels with playground data and distance //
  const now = new Date();
  const enrichedDuels = openDuels
    .map(duel => {
      const playground = playgroundsMap[duel.playground_id];
      if (!playground) return null;

      let distance = null;
      if (userLocation && playground.coords_json) {
        distance = calculateDistance(
          userLocation.lat,
          userLocation.lng,
          playground.coords_json.lat,
          playground.coords_json.lng
        );
      }

      const openExpiresAt = getOpenExpiresAt(duel);

      return {
        ...duel,
        playground,
        distance,
        openExpiresAt
      };
    })
    .filter((duel) => duel && duel.openExpiresAt && duel.openExpiresAt > now)
    .sort((a, b) => {
      // Sort by distance if available, else by time remaining
      if (a.distance !== null && b.distance !== null) return a.distance - b.distance;
      if (a.openExpiresAt && b.openExpiresAt) return a.openExpiresAt - b.openExpiresAt;
      if (a.openExpiresAt) return -1;
      if (b.openExpiresAt) return 1;
      return 0;
    });

  // Function to join an open duel
  const handleJoinDuel = async (duel) => {
    setJoiningDuelId(duel.id);

    try {
      // 1. Get user location
      toast.info('Obtention de votre position...');
      const location = await getLocation({ useCache: true, timeout: 5000 });

      // 2. Create check-in
      toast.info('Vérification de votre présence...');
      const checkinData = await createCheckIn({
        playground_id: duel.playground_id,
        user_lat: location.lat,
        user_lng: location.lng
      });

      if (!checkinData.is_valid) {
        toast.error(`Trop loin ! Distance: ${checkinData.distance_m}m. Vous devez être au terrain.`);
        setJoiningDuelId(null);
        return;
      }

      // 3. Play the duel
      toast.info('Démarrage du match...');
      const { data: { session } } = await supabase.auth.getSession();
      const playResponse = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/playOpenDuel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify({ duel_id: duel.id, checkin_id: checkinData.checkin_id })
      });
      const playResult = await playResponse.json();
      if (!playResponse.ok) throw new Error(playResult.error || 'Erreur lors du lancement du duel');

      toast.success('Match démarré ! Soumets le score quand le match est terminé.');
      queryClient.invalidateQueries({ queryKey: ['openDuels'] });
      queryClient.invalidateQueries({ queryKey: ['openDuelsCount'] });
      queryClient.invalidateQueries({ queryKey: ['playgrounds'] });
      queryClient.invalidateQueries({ queryKey: ['duel'] });

      // Rediriger vers la page du terrain
      navigate(`/court/${duel.playground_id}`);
      if (onClose) onClose();
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.message || 'Erreur lors de la connexion au match';
      toast.error(errorMsg);
    } finally {
      setJoiningDuelId(null);
    }
  };

  if (!user) {
    return (
      <div className="fixed inset-0 z-50 bg-[#0A0B10] flex items-center justify-center">
        <div className="text-center">
          <p className="text-white mb-4">Connectez-vous pour voir les duels ouverts</p>
          <button
            onClick={() => navigate('/login')}
            className="bg-[#6E2BFF] hover:bg-[#8B5CFF] text-white px-6 py-2 rounded-lg"
          >
            Connexion
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-[#0A0B10] overflow-auto">
      <div className="p-4 md:p-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-start mb-6">
          <div className="flex items-start gap-3">
            {!onClose && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(-1)}
                className="text-white hover:text-[#6E2BFF] p-2 -ml-2"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
            )}
            <div>
              <h2 className="text-lg font-black uppercase tracking-wide text-white">
                <Swords className="inline w-5 h-5 mr-2 mb-1" />
                Duels Ouverts
              </h2>
              <p className="text-[#B3B6C2] text-xs md:text-sm mt-2">
                Terrains contestables a proximite
              </p>
            </div>
          </div>
          {onClose && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="text-white hover:text-[#6E2BFF]"
            >
              <X className="w-5 h-5" />
            </Button>
          )}
        </div>

        {/* Explanation banner */}
        <div className="bg-amber-500/10 border border-amber-600/40 rounded-lg p-4 mb-6 space-y-2">
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-[#B3B6C2] space-y-1">
              <p className="text-amber-400 font-bold text-sm">Comment ça marche ?</p>
              <p>Un duel ouvert = le défenseur a refusé ou ignoré le défi, libérant sa place.</p>
              <p>N'importe quel joueur présent sur le terrain peut prendre la place du défenseur et lancer le match.</p>
              <p className="font-semibold text-white">Le gagnant capture le terrain. Si personne ne joue, l'attaquant gagne par défaut.</p>
            </div>
          </div>
        </div>

        {/* List of OPEN duels */}
        <div className="max-w-4xl mx-auto">
          {enrichedDuels.length === 0 ? (
            <div className="text-center py-12">
              <Swords className="w-12 h-12 text-[#6E2BFF] mx-auto mb-4 opacity-50" />
              <p className="text-[#B3B6C2]">Aucun duel ouvert pour le moment</p>
              <p className="text-[#B3B6C2] text-sm mt-2">
                Les terrains dont les owners ignorent les défis apparaîtront ici
              </p>
            </div>
          ) : (
            <div className="grid gap-3 md:gap-4">
              {enrichedDuels.map(duel => {
                const openExpiresAt = duel.openExpiresAt;
                const timeRemaining = formatTimeRemaining(openExpiresAt);
                const isExpired = false;

                return (
                  <Card
                    key={duel.id}
                    className="bg-gradient-to-br from-[#13151D] to-[#1B1E28] border-2 border-amber-600 p-4 md:p-5 hover:border-amber-500 hover:shadow-lg hover:shadow-amber-600/20 transition-all"
                  >
                    <div className="space-y-3">
                      {/* Playground Name & Status */}
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base md:text-lg font-black text-white mb-1">
                            {duel.playground?.name || 'Terrain'}
                          </h3>
                          {duel.playground?.address && (
                            <p className="text-[#B3B6C2] text-xs flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {duel.playground.city || duel.playground.address}
                            </p>
                          )}
                        </div>
                        <Badge className="bg-amber-500/20 border border-amber-500 text-amber-400 text-xs font-bold uppercase px-2 py-1 flex-shrink-0">
                          OUVERT
                        </Badge>
                      </div>

                      {/* Info Grid */}
                      <div className="grid grid-cols-2 gap-3 bg-[#0A0B10] rounded-lg p-3 border border-amber-600/30">
                        {duel.distance !== null && (
                          <div>
                            <p className="text-xs uppercase tracking-widest text-[#B3B6C2] font-bold mb-1">
                              Distance
                            </p>
                            <p className="text-white font-bold">
                              {duel.distance < 1
                                ? `${Math.round(duel.distance * 1000)}m`
                                : `${duel.distance.toFixed(1)}km`}
                            </p>
                          </div>
                        )}
                        <div>
                          <p className="text-xs uppercase tracking-widest text-[#B3B6C2] font-bold mb-1">
                            Temps restant
                          </p>
                          <p className={`font-bold ${isExpired ? 'text-red-500' : 'text-amber-400'}`}>
                            {timeRemaining}
                          </p>
                        </div>
                      </div>

                      {/* Attacker Info */}
                      <div className="bg-[#0A0B10] rounded-lg p-3 border border-[#6E2BFF]/30">
                        <p className="text-xs uppercase tracking-widest text-[#B3B6C2] font-bold mb-2">
                          <Users className="inline w-3 h-3 mr-1" />
                          Attaquant original
                        </p>
                        <p className="text-white font-bold">{duel.attacker_name}</p>
                        {duel.attacker_user_id !== user.id && (
                          <p className="text-xs text-amber-400/70 mt-1">Si tu rejoins, tu prends la place du défenseur.</p>
                        )}
                      </div>

                      {/* What happens next */}
                      <div className="text-xs text-[#9CA0AB] space-y-0.5 px-1">
                        <p className="font-semibold text-[#B3B6C2]">Que se passe-t-il ensuite ?</p>
                        <p>→ Un joueur rejoint → il devient défenseur, le match commence</p>
                        <p>→ L'attaquant original lance → le match commence avec le défenseur d'origine</p>
                        <p>→ Personne ne joue → l'attaquant gagne par défaut</p>
                      </div>

                      {/* Action Button — original attacker CAN play (backend allows it) */}
                      {duel.defender_user_id !== user.id && (
                        <>
                          <Button
                            onClick={() => handleJoinDuel(duel)}
                            disabled={joiningDuelId === duel.id}
                            className="w-full bg-amber-600 hover:bg-amber-500 text-white font-bold uppercase transition-all"
                          >
                            {joiningDuelId === duel.id
                              ? 'Chargement...'
                              : duel.attacker_user_id === user.id
                                ? 'Lancer le match'
                                : 'Rejoindre le match'}
                          </Button>
                          <p className="text-center text-[#9CA0AB] text-xs">
                            Tu dois être physiquement sur le terrain (check-in automatique).
                          </p>
                        </>
                      )}
                      {duel.defender_user_id === user.id && (
                        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                          <p className="text-center text-red-400 text-sm font-semibold">
                            Tu es le défenseur de ce duel. Tu ne peux pas le rejoindre.
                          </p>
                        </div>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
